import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReceivingContext } from '../../api/endpoints.js';
import { ActionButton, Alert } from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { requestedTestName, requestedTestStandard } from './requested-tests.js';

/**
 * "Work order context" — what the receiving desk sees about the job a sample
 * arrived for (D194).
 *
 * READ-ONLY AND DERIVED. Every value comes from the work order, its client, its
 * project and its location as they are on file; nothing is typed again at the
 * counter, and nothing here can be edited from it.
 *
 * COMPACT FIRST (D288). The job number, client, project, request date and how
 * many services were ordered are always shown, in one band; the rest — address,
 * site, references, representative, contact and the service list — opens on
 * request, so the person reaches the sample fields without scrolling past a
 * card they have already read.
 *
 * REQUESTED SERVICES ARE NOT TEST ASSIGNMENTS. They are what the client
 * ordered. A test is assigned to a sample later, after its acceptance decision,
 * through the assignment workflow — which this card does not touch.
 *
 * NO REQUIRED WEIGHT IS SHOWN (D291). The catalogue stores none (`test_catalogue`
 * has no such column), and a line saying "Not configured" under every service
 * told the receiver nothing. When a validated value exists, it belongs here.
 */
export function WorkOrderContextCard({ context }: { readonly context: ReceivingContext }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const titleId = useId();
  const detailsId = useId();
  const [open, setOpen] = useState(false);
  const notRecorded = t('common.notRecorded');

  const contactNumber = context.requestingParty.phone ?? context.client.contactPhone;
  const contactFromClient =
    context.requestingParty.phone === null && context.client.contactPhone !== null;
  const references = [context.workOrder.clientReference, context.workOrder.purchaseOrderNumber]
    .filter((value): value is string => Boolean(value));
  const address = [context.client.address, context.client.city]
    .filter(Boolean)
    .join(t('samples.fieldSeparator'));
  const place = context.location
    ? [context.location.name, context.location.city].filter(Boolean).join(t('samples.fieldSeparator'))
    : null;
  const project = context.project
    ? (context.project.name ?? context.project.reference ?? notRecorded)
    : t('common.none');

  return (
    <section
      aria-labelledby={titleId}
      className="min-w-0 rounded-medium border border-default-300 bg-default-50 p-3 sm:p-4"
      data-testid="work-order-context"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-gutter gap-y-2">
        <h3 id={titleId} className="text-body font-semibold text-default-900">
          {t('receiving.contextTitle')}
        </h3>
        <ActionButton
          priority="tertiary"
          size="sm"
          aria-expanded={open}
          aria-controls={detailsId}
          onPress={() => setOpen((value) => !value)}
        >
          {open ? t('receiving.hideDetails') : t('receiving.showDetails')}
        </ActionButton>
      </div>
      {context.workOrder.isCancelled ? (
        <div className="mt-stack">
          <Alert tone="warning" title={t('receiving.cancelledWorkOrder')} />
        </div>
      ) : null}

      {/* The band: what a receiver checks before every delivery. */}
      <dl
        className="mt-2 grid grid-cols-1 gap-x-gutter gap-y-2 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="work-order-summary"
      >
        <Fact label={t('receiving.workOrderNumber')}>
          <span dir="ltr" className="font-mono">{context.workOrder.workOrderNumber}</span>
        </Fact>
        <Fact label={t('receiving.client')}>
          {context.client.name ?? notRecorded}
          {context.client.clientNumber ? (
            <span className="ms-1 text-caption text-default-600" dir="ltr">
              {context.client.clientNumber}
            </span>
          ) : null}
        </Fact>
        <Fact label={t('receiving.project')}>{project}</Fact>
        <Fact label={t('receiving.requestDate')}>
          {formatDate(context.workOrder.requestDate, { locale }) ?? notRecorded}
          <span className="block text-caption text-default-600">
            {t('receiving.servicesCount', { count: context.services.length })}
          </span>
        </Fact>
      </dl>

      <div id={detailsId} className="min-w-0">
        {open ? (
          <div className="mt-stack border-t border-default-200 pt-stack" data-testid="work-order-details">
            <p className="text-caption text-default-600">{t('receiving.contextHint')}</p>
            <dl className="mt-stack grid grid-cols-1 gap-x-gutter gap-y-3 sm:grid-cols-2">
              <Fact label={t('receiving.address')}>{address || notRecorded}</Fact>
              <Fact label={t('receiving.location')}>{place || t('common.none')}</Fact>
              <Fact label={t('receiving.references')}>
                {references.length > 0 ? <span dir="auto">{references.join(' / ')}</span> : notRecorded}
              </Fact>
              <Fact label={t('receiving.representative')}>
                {context.requestingParty.name ?? notRecorded}
              </Fact>
              <Fact label={t('receiving.contactNo')}>
                {contactNumber ? (
                  <>
                    <span dir="ltr">{contactNumber}</span>
                    {contactFromClient ? (
                      <span className="ms-1 text-caption text-default-600">
                        {t('receiving.contactFromClient')}
                      </span>
                    ) : null}
                  </>
                ) : (
                  notRecorded
                )}
              </Fact>
            </dl>

            <h4 className="mt-gutter text-body font-semibold text-default-900">
              {t('receiving.services')}
            </h4>
            {context.services.length === 0 ? (
              <p className="mt-1 text-body text-default-600">{t('receiving.noServices')}</p>
            ) : (
              <ul className="mt-2 flex flex-col gap-1">
                {context.services.map((service) => {
                  const standard = requestedTestStandard(service.referenceStandard, service.standardVersion);
                  return (
                    <li key={service.id} className="min-w-0 text-body text-default-900">
                      <span className="me-1 text-default-600">{service.lineNumber}.</span>
                      {standard ? (
                        <bdi dir="ltr" className="me-2 font-semibold">{standard}</bdi>
                      ) : null}
                      {requestedTestName(service.description.ar, service.description.en, locale) ??
                        notRecorded}
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="mt-2 text-caption text-default-600">{t('receiving.servicesNotAssignments')}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption text-default-600">{label}</dt>
      <dd className="mt-0.5 break-words text-body text-default-900">{children}</dd>
    </div>
  );
}
